import { useCallback, useEffect, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { callingService } from '../services/callingService';
import type { IncomingCallEvent } from '../types/calling';

const PENDING_ACCEPT_KEY = 'vag.pendingAcceptCall';

type IncomingSnapshot = {
  incomingCall: IncomingCallEvent | null;
};

let incomingCall: IncomingCallEvent | null = null;
let boundSocket: Socket | null = null;
/** Survives React Strict Mode remounts (sessionStorage alone is cleared too early). */
let pendingAcceptCall: IncomingCallEvent | null = null;
/** Prevents double joinCall when Call page effects re-run. */
let pendingAcceptInFlightId: string | null = null;
const subscribers = new Set<() => void>();

function emitChange() {
  subscribers.forEach((listener) => listener());
}

function setIncomingCall(next: IncomingCallEvent | null) {
  incomingCall = next;
  emitChange();
}

function getSnapshot(): IncomingSnapshot {
  return { incomingCall };
}

function bindIncomingCallListeners() {
  const socket = callingService.getSocket();
  if (!socket || boundSocket === socket) return;
  boundSocket = socket;

  const onIncoming = (event: IncomingCallEvent) => {
    if (!event?.callId) return;
    setIncomingCall(event);
  };

  const clearIfMatch = (event: { callId?: string }) => {
    if (!event?.callId || !incomingCall) return;
    if (event.callId === incomingCall.callId) setIncomingCall(null);
  };

  socket.on('call:incoming', onIncoming);
  socket.on('call:ended', clearIfMatch);
  socket.on('call:rejected', clearIfMatch);
  socket.on('call:joined', (event: { callId?: string }) => {
    // Callee joined from this client — clear the ring banner.
    if (event?.callId && incomingCall?.callId === event.callId) {
      setIncomingCall(null);
    }
  });
}

export function ensureIncomingCallListeners() {
  const token = localStorage.getItem('token');
  if (!token) return;
  callingService.connect(token);
  bindIncomingCallListeners();
}

export function clearIncomingCallRing(callId?: string) {
  if (!incomingCall) return;
  if (callId == null || incomingCall.callId === callId) {
    setIncomingCall(null);
  }
}

function isValidIncomingCall(value: unknown): value is IncomingCallEvent {
  if (!value || typeof value !== 'object') return false;
  const parsed = value as Partial<IncomingCallEvent>;
  return (
    typeof parsed.callId === 'string' &&
    (parsed.type === 'direct' || parsed.type === 'group') &&
    typeof parsed.hostUserId === 'number'
  );
}

export function storePendingAcceptCall(call: IncomingCallEvent) {
  pendingAcceptCall = call;
  pendingAcceptInFlightId = null;
  sessionStorage.setItem(PENDING_ACCEPT_KEY, JSON.stringify(call));
}

export function peekPendingAcceptCall(): IncomingCallEvent | null {
  if (pendingAcceptCall && isValidIncomingCall(pendingAcceptCall)) {
    return pendingAcceptCall;
  }
  try {
    const raw = sessionStorage.getItem(PENDING_ACCEPT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IncomingCallEvent;
    if (!isValidIncomingCall(parsed)) return null;
    pendingAcceptCall = parsed;
    return parsed;
  } catch {
    return null;
  }
}

/** @deprecated Prefer peek + clearPendingAcceptCall after join starts. */
export function takePendingAcceptCall(): IncomingCallEvent | null {
  const parsed = peekPendingAcceptCall();
  clearPendingAcceptCall();
  return parsed;
}

export function clearPendingAcceptCall() {
  pendingAcceptCall = null;
  pendingAcceptInFlightId = null;
  sessionStorage.removeItem(PENDING_ACCEPT_KEY);
}

/** Claim a pending accept for processing; safe across Strict Mode remounts. */
export function claimPendingAcceptCall(): IncomingCallEvent | null {
  const pending = peekPendingAcceptCall();
  if (!pending) return null;
  if (pendingAcceptInFlightId === pending.callId) return null;
  pendingAcceptInFlightId = pending.callId;
  return pending;
}

export function releasePendingAcceptClaim(callId?: string) {
  if (callId == null || pendingAcceptInFlightId === callId) {
    pendingAcceptInFlightId = null;
  }
}

export function peekIncomingCall(): IncomingCallEvent | null {
  return incomingCall;
}

export function resetIncomingCallRing() {
  incomingCall = null;
  boundSocket = null;
  clearPendingAcceptCall();
  emitChange();
}

export function useIncomingCallRing() {
  const [snapshot, setSnapshot] = useState<IncomingSnapshot>(getSnapshot);

  useEffect(() => {
    const sync = () => setSnapshot(getSnapshot());
    subscribers.add(sync);
    ensureIncomingCallListeners();
    sync();
    return () => {
      subscribers.delete(sync);
    };
  }, []);

  const rejectIncomingCall = useCallback(() => {
    const call = incomingCall;
    if (!call) return;
    callingService.rejectCall(call.callId);
    setIncomingCall(null);
  }, []);

  return {
    incomingCall: snapshot.incomingCall,
    rejectIncomingCall,
    clearIncomingCallRing,
    storePendingAcceptCall,
  };
}
